import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import AuthService from '../services/authService';
import { clearTokens, getAccessToken, setTokens } from '../services/tokenStore';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      setLoading(false);
      return;
    }
    AuthService.getMe()
      .then((userData) => {
        setUser(userData);
      })
      .catch(() => {
        clearTokens();
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const result = await AuthService.login(email, password);
    setTokens(result.accessToken, result.refreshToken);
    setUser(result.user);
    return result.user;
  }, []);

  const signup = useCallback(async ({ name, email, password, role, companyName, country }) => {
    const result = await AuthService.signup({ name, email, password, role, companyName, country });
    return { user: { name, email, role, companyName, country, human_id: result.user?.human_id, id: result.user?.id }, devOtp: result.devOtp };
  }, []);

  const verifyOtp = useCallback(async (email, otp) => {
    return AuthService.verifyOtp(email, otp);
  }, []);

  const resendOtp = useCallback(async (email) => {
    return AuthService.resendOtp(email);
  }, []);

  const finalizeAuth = useCallback(({ accessToken, refreshToken, user }) => {
    setTokens(accessToken, refreshToken);
    setUser(user);
  }, [setUser]);

  const logout = useCallback(async () => {
    try {
      await AuthService.logout();
    } catch {
      // ignore logout errors
    }
    clearTokens();
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, isAuthenticated: !!user,
      login, signup, verifyOtp, resendOtp, logout, setUser, finalizeAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export default AuthContext;

